import { useUpdateNode, useWorkflow } from "@/api/workflow-hooks";
import { Button } from "@/components/button";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/resizable";
import dagre from "@dagrejs/dagre";
import { type NodeData, isGroupNode } from "@repo/shared/workflow";
import {
  Background,
  type Connection,
  Controls,
  MarkerType,
  type NodeMouseHandler,
  type NodeTypes,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import { PlusIcon } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import "@xyflow/react/dist/style.css";
import _ from "lodash";
import { NodeEditor } from "./NodeEditor";
import { FoundationNodeTypes } from "./nodes";
import {
  type NodeDisplay,
  type RFEdge,
  type RFNode,
  useEditorNodes,
  useEditorState,
} from "./state";

const mapWorkflowToReactFlow = (
  editorNodes: ReturnType<typeof useEditorNodes>,
): {
  nodes: RFNode[];
  edges: RFEdge[];
} => {
  const nodes: RFNode[] = [];
  const edges: RFEdge[] = [];

  for (const node of Object.values(editorNodes)) {
    nodes.push({
      id: node.node.id,
      position: { x: 0, y: 0 }, // Initial position, will be updated by dagre
      data: node,
      hidden: node.hide,
      type: isGroupNode(node.node) ? "foundation-group" : "base",
    });
  }

  // Create edges
  for (const { node: data, displayDependencyIds } of Object.values(
    editorNodes,
  )) {
    for (const targetId of displayDependencyIds) {
      edges.push({
        id: `e-${targetId}-${data.id}`,
        source: targetId,
        target: data.id,
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed },
        type: "smoothstep",
      });
    }
  }

  // Use dagre to calculate node positions
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Set graph options
  const nodeWidth = 200;
  dagreGraph.setGraph({
    rankdir: "TB",
    nodesep: 50, // Add horizontal separation between nodes
    ranksep: 100, // Vertical separation between ranks
    marginx: 20, // Add margin to prevent nodes from touching edges
    marginy: 20,
  });

  // Add nodes to dagre graph
  for (const node of nodes) {
    if (node.hidden) continue;

    // Calculate approximate height based on content
    let height = 80; // Base height
    const nodeData = editorNodes[node.id];

    if (isGroupNode(nodeData.node)) {
      // For group nodes, add height for each child
      height = 100 + nodeData.node.dependencyIds.length * 40;
    } else {
      // For regular nodes, adjust height based on content
      const nameLength = nodeData.node.name.length;
      if (nameLength > 30) {
        height += 20; // Add extra height for longer names
      }
    }

    dagreGraph.setNode(node.id, {
      width: nodeWidth,
      height: height,
    });
  }

  // Add edges to dagre graph
  for (const edge of edges) {
    const sourceHidden = editorNodes[edge.source]?.hide;
    const targetHidden = editorNodes[edge.target]?.hide;
    if (sourceHidden || targetHidden) continue;

    dagreGraph.setEdge(edge.source, edge.target);
  }

  // Calculate layout using dagre
  dagre.layout(dagreGraph);

  // Apply calculated positions to nodes
  for (const node of nodes) {
    const dagreNode = dagreGraph.node(node.id);

    if (dagreNode) {
      // We need to position the node at the center
      node.position = {
        x: dagreNode.x - nodeWidth / 2,
        y: dagreNode.y - dagreNode.height / 2,
      };
    }
  }

  return { nodes, edges };
};

// GraphPanel Component
interface GraphPanelProps {
  editorNodes: ReturnType<typeof useEditorNodes>;
  selectedNodeId: string | null;
  onNodeSelect: (nodeId: string | null) => void;
}

const GraphPanel: React.FC<GraphPanelProps> = ({
  editorNodes,
  selectedNodeId,
  onNodeSelect,
}) => {
  // Use useMemo to compute nodes and edges
  const { nodes: computedNodes, edges: computedEdges } = useMemo(() => {
    const result = mapWorkflowToReactFlow(editorNodes);
    return result;
  }, [editorNodes]);

  // Initialize state with computed values
  const [nodes, setNodes, onNodesChange] = useNodesState(computedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(computedEdges);

  // Update nodes and edges when computed values change
  useEffect(() => {
    setNodes(computedNodes);
    setEdges(computedEdges);
  }, [computedNodes, computedEdges, setNodes, setEdges]);

  // Keep the node selection in sync
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        selected: node.id === selectedNodeId,
      })),
    );
  }, [selectedNodeId, setNodes]);

  // Handle node click
  const onNodeClick: NodeMouseHandler = useCallback(
    (_, node) => {
      onNodeSelect(node.id);
    },
    [onNodeSelect],
  );

  // Handle connecting nodes
  const onConnect = useCallback(
    (connection: Connection) => {
      // In a real app, you would also update the workflow data via API
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed },
            type: "smoothstep",
          },
          eds,
        ),
      );
    },
    [setEdges],
  );

  return (
    <div className="h-full">
      <ReactFlow
        proOptions={{ hideAttribution: true }}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        nodeTypes={FoundationNodeTypes as NodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
          maxZoom: 1.5,
          minZoom: 0.1,
        }}
        // nodesDraggable={false}
        nodesConnectable={true}
        elementsSelectable={true}
      >
        <Background />
        <Controls />
        <Panel position="top-right">
          <Button size="sm" variant="outline">
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Node
          </Button>
        </Panel>
      </ReactFlow>
    </div>
  );
};

interface EditorPanelProps {
  workflowId: string;
  node: NodeDisplay | null | undefined;
  onUpdateNode: (node: NodeData) => void;
}

const EditorPanel: React.FC<EditorPanelProps> = ({
  workflowId,
  node,
  onUpdateNode,
}) => {
  if (!node) {
    return (
      <div className="flex items-center justify-center h-full p-4 text-muted-foreground">
        Select a node to view and edit its properties
      </div>
    );
  }

  return (
    <div className="h-full">
      <NodeEditor
        workflowId={workflowId}
        className="m-4"
        // nodeNames={nodeNames}
        node={node}
        onChange={onUpdateNode}
      />
    </div>
  );
};

const DAGEditor: React.FC = () => {
  const params = useParams<{ id: string }>();
  const workflowId = params.id as string;

  const { data: workflowData, isLoading } = useWorkflow(workflowId);
  const stateWorkflowId = useEditorState((state) => state.workflow?.id);
  const initEditorState = useEditorState((state) => state.init);
  const selectedNodeId = useEditorState((state) => state.selectedNodeId);
  const setSelectedNodeId = useEditorState((state) => state.selectNode);
  const editorNodes = useEditorNodes();

  useEffect(() => {
    if (!workflowData?.workflow) {
      return;
    }

    // Initialize the state if the workflow id has changed
    if (stateWorkflowId !== workflowId) {
      initEditorState(workflowData.workflow);
    }
  }, [workflowData, initEditorState, stateWorkflowId, workflowId]);

  const selectedNode = useMemo(
    () => editorNodes[selectedNodeId ?? ""],
    [editorNodes, selectedNodeId],
  );
  const updateNode = useUpdateNode(workflowId);

  const handleNodeUpdate = useCallback(
    (updatedNode: NodeData) => {
      if (updatedNode.id) {
        updateNode.mutate({
          nodeId: updatedNode.id,
          update: updatedNode,
        });
      }
    },
    [updateNode],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading workflow...
      </div>
    );
  }

  if (!workflowData?.workflow) {
    return (
      <div className="flex items-center justify-center h-screen">
        Workflow not found
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <div className="h-[calc(100dvh-4rem)] w-full">
        <ResizablePanelGroup direction="horizontal">
          <ResizablePanel defaultSize={40} minSize={20}>
            <EditorPanel
              workflowId={workflowId}
              node={selectedNode}
              onUpdateNode={handleNodeUpdate}
            />
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel defaultSize={60}>
            <GraphPanel
              editorNodes={editorNodes}
              selectedNodeId={selectedNodeId}
              onNodeSelect={setSelectedNodeId}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </ReactFlowProvider>
  );
};

export default DAGEditor;
